/**
 * Application Designer — the saved layout document.
 *
 * One format for both layout modes, and the single place that decides what a
 * stored layout is allowed to contain. The invariants are the ones Patient
 * Summary's Design View already enforced, generalized:
 *
 *   - The DESCRIPTOR is the authority. Every document is rebuilt from the
 *     page's element manifest and saved values are merged in, so an unknown id
 *     is dropped rather than preserved and a new element appears at its declared
 *     default without a migration.
 *   - A document holds layout and presentation ONLY. Clinical fields, pixel
 *     geometry, bindings and permission decisions in an incoming payload are
 *     discarded by reconstruction rather than by a denylist.
 *   - Cosmetics are SPARSE. `style` is present only when an element actually
 *     carries a non-default cosmetic, so a page nobody has styled saves exactly
 *     the keys it saved before the Application Designer existed.
 *
 * `cards` is accepted as an alias for `items` on read because the native Patient
 * Summary endpoint has always spoken `cards`. Nothing writes both.
 */

import { LAYOUT_MODE_GRID } from "./layoutModes";
import { sanitizeElementCosmetics } from "./cosmetics";
import { compactFlowOrders, clampFlowColumn, toFlowOrder } from "./flowLayout";
import { normalizeGridItems, gridBoundsOf } from "./gridLayout";
import { descriptorElement } from "./pageDescriptor";

export const LAYOUT_DOCUMENT_VERSION = 1;

export function layoutDocumentBounds(descriptor) {
  return gridBoundsOf({ columns: descriptor.columns, rows: descriptor.rows });
}

function attachStyle(item, rawStyle, element) {
  const style = sanitizeElementCosmetics(rawStyle, element.cosmetics);
  if (style) item.style = style;
  return item;
}

function flowItem(descriptor, element, raw = {}) {
  return attachStyle(
    {
      id: element.id,
      column: clampFlowColumn(raw.column, element.column, descriptor.columns),
      order: toFlowOrder(raw.order, element.order),
      visible: raw.visible !== false,
    },
    raw.style,
    element,
  );
}

function gridItem(element, raw = {}) {
  return attachStyle(
    {
      id: element.id,
      col: Math.max(1, Number.parseInt(raw.col, 10) || element.col),
      row: Math.max(1, Number.parseInt(raw.row, 10) || element.row),
      width: Math.max(1, Number.parseInt(raw.width, 10) || element.width),
      height: Math.max(1, Number.parseInt(raw.height, 10) || element.height),
      visible: raw.visible !== false,
    },
    raw.style,
    element,
  );
}

export function layoutItem(descriptor, id, raw = {}) {
  const element = descriptorElement(descriptor, id);
  if (!element) return null;
  return descriptor.mode === LAYOUT_MODE_GRID
    ? gridItem(element, raw)
    : flowItem(descriptor, element, raw);
}

export function defaultLayoutItems(descriptor) {
  return descriptor.elements.map((element) => layoutItem(descriptor, element.id));
}

export function defaultLayoutDocument(descriptor) {
  return {
    page: descriptor.page,
    version: LAYOUT_DOCUMENT_VERSION,
    mode: descriptor.mode,
    organizationId: null,
    source: "default",
    items: normalizeLayoutItems(descriptor, defaultLayoutItems(descriptor)),
  };
}

/**
 * Rebuilds the item list from the descriptor. The first occurrence of an id
 * wins, which makes a duplicated entry in a stored payload harmless, and every
 * element the descriptor declares is present in declaration order.
 */
export function normalizeLayoutItems(descriptor, rawItems) {
  const incoming = Array.isArray(rawItems) ? rawItems : [];
  const byId = new Map();
  incoming.forEach((raw) => {
    const id = typeof raw?.id === "string" ? raw.id.trim() : "";
    if (!id || byId.has(id)) return;
    const item = layoutItem(descriptor, id, raw);
    if (item) byId.set(id, item);
  });
  descriptor.elements.forEach((element) => {
    if (!byId.has(element.id)) byId.set(element.id, layoutItem(descriptor, element.id));
  });
  const ordered = descriptor.elementIds.map((id) => byId.get(id));
  return descriptor.mode === LAYOUT_MODE_GRID
    ? normalizeGridItems(ordered, layoutDocumentBounds(descriptor))
    : compactFlowOrders(ordered, descriptor.columns);
}

export function normalizeLayoutDocument(descriptor, raw) {
  const rawItems = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.cards)
      ? raw.cards
      : Array.isArray(raw)
        ? raw
        : [];
  return {
    page: descriptor.page,
    version: LAYOUT_DOCUMENT_VERSION,
    mode: descriptor.mode,
    organizationId: raw?.organizationId || null,
    source: raw?.source || "default",
    items: normalizeLayoutItems(descriptor, rawItems),
  };
}

/**
 * What gets sent to the server. Deliberately narrower than the in-memory
 * document: no organization, no source, no mode — the server resolves those
 * from the page id and its own copy of the manifest, so a client cannot widen
 * its own scope by editing a payload.
 */
export function layoutDocumentPayload(descriptor, items) {
  return {
    page: descriptor.page,
    version: LAYOUT_DOCUMENT_VERSION,
    items: normalizeLayoutItems(descriptor, items),
  };
}

export function findLayoutItem(items, id) {
  return (items || []).find((item) => item.id === id) || null;
}

export function updateLayoutItem(items, id, patch) {
  return (items || []).map((item) => (item.id === id ? { ...item, ...patch } : item));
}

/**
 * Applies a cosmetic edit, removing `style` entirely when the element is back
 * to native defaults so an unstyled element never carries an empty object.
 */
export function styleLayoutItem(items, id, style, acceptedOptIn) {
  return (items || []).map((item) => {
    if (item.id !== id) return item;
    const next = { ...item };
    const sanitized = sanitizeElementCosmetics(style, acceptedOptIn);
    if (sanitized) next.style = sanitized;
    else delete next.style;
    return next;
  });
}
